
import { Component, Emit, Prop, Watch } from 'vue-property-decorator';
import VFormBuilder from '@/components/shared/form/VFormBuilder.vue';
import { TranslateResult } from 'vue-i18n';
import { mixins } from 'vue-class-component';
import CustomerGroup from '@/mixins/CustomerGroup';
import StackedForm from '@/mixins/StackedForm';
import Countries from '@/mixins/Countries';
import { Venue } from '@/interfaces/models/Venue';
import { Article } from '@/interfaces/models/Article';
import { VenueLabel } from '@/interfaces/models/VenueLabel';
import { FormItem } from '@/interfaces/components/FormItem';
import { PromoCode } from '@/interfaces/models/PromoCode';
import { PiggyReward } from '@/interfaces/models/PiggyReward';
import { Tag } from '@/interfaces/models/Tag';
import { InputType } from '@/enums/InputType';
import { PromoCodeType } from '@/enums/PromoCodeType';
import { Country } from '@/enums/Country';
import { CustomerGroup as CustomerGroupEnum } from '@/enums/CustomerGroup';
import { PromoCodeUsageFrequency } from '@/enums/PromoCodeUsageFrequency';
import { Permission } from '@/enums/Permission';
import { MainCategoryType } from '@/enums/MainCategoryType';
import { venueFilter } from '@/util/helper';

@Component({
  components: { VFormBuilder },
})
export default class GeneralForm extends mixins(CustomerGroup, StackedForm, Countries) {
  @Prop({ type: Array, default: () => [] }) public venues!: Venue[];
  @Prop({ type: Array, default: () => [] }) public labels!: VenueLabel[];
  @Prop({ type: Array, default: () => [] }) public articles!: Article[];
  @Prop({ type: Array, default: () => [] }) public rewards!: PiggyReward[];
  @Prop({ type: Array, required: true }) public tags!: Tag[];
  @Prop({ type: Boolean, required: true }) public editing!: boolean;
  @Prop({ type: String, default: null }) public customerGroup!: CustomerGroup;
  @Prop({ type: String, default: null }) public linkedCustomerGroup!: CustomerGroup;
  @Prop({ type: Boolean, default: false }) public multiple!: boolean;
  @Prop({ type: Boolean, default: false }) public teedeli!: boolean;

  private origTypeItems: Array<{ text: any; value: string }> = [
    { text: this.$t('promo.types.absolute'), value: PromoCodeType.Absolute },
    { text: this.$t('promo.types.relative'), value: PromoCodeType.Relative },
    { text: this.$t('promo.types.deliveryFee'), value: PromoCodeType.Delivery_Fee },
    { text: this.$t('promo.types.bogo'), value: PromoCodeType.Bogo },
    { text: this.$t('promo.types.giftCard'), value: PromoCodeType.Gift_Card },
    { text: this.$t('promo.types.freeArticle'), value: PromoCodeType.Free_Article },
    { text: this.$t('promo.types.dependencyFreeArticle'), value: PromoCodeType.Dependency_Free_Article },
    { text: this.$t('promo.types.cheapestFreeArticle'), value: PromoCodeType.Cheapest_Free_Article },
    { text: this.$t('promo.types.void'), value: PromoCodeType.Void },
    { text: this.$t('promo.types.absoluteArticle'), value: PromoCodeType.Absolute_Article },
    { text: this.$t('promo.types.relativeArticle'), value: PromoCodeType.Relative_Article },
    { text: this.$t('promo.types.singleAbsoluteArticle'), value: PromoCodeType.Single_Absolute_Article },
    { text: this.$t('promo.types.singleRelativeArticle'), value: PromoCodeType.Single_Relative_Article },
    { text: this.$t('promo.types.multipleAbsoluteArticle'), value: PromoCodeType.Multiple_Absolute_Article },
    { text: this.$t('promo.types.multipleRelativeArticle'), value: PromoCodeType.Multiple_Relative_Article },
    { text: this.$t('promo.types.unlockArticles'), value: PromoCodeType.Unlock_Articles },
    {
      text: this.$t('promo.types.cheapestHalfArticleDiscountRelative'),
      value: PromoCodeType.Cheapest_Half_Article_Discount_Relative,
    },
    { text: this.$t('promo.types.coupon'), value: PromoCodeType.Coupon },
    { text: this.$t('promo.types.multipleFreeArticles'), value: PromoCodeType.Multiple_Free_Articles },
  ];
  // TODO - refactor logic
  private typeItems: Array<{ text: any; value: string }> = this.teedeli
    ? [...this.origTypeItems, ...this.teedeliTypes]
    : this.origTypeItems;

  private isFreeArticleMultipleSelection: boolean = true;
  private isMultipleFreeArticleMultipleSelection: boolean = false;
  private isUnlimited: boolean = false;
  private isUnlimitedAmount: boolean = false;

  get initVals() {
    return { ...this.initialValues } as any;
  }

  get canUpdateTimeframe() {
    if (this.$isAdmin()) {
      return true;
    }

    return this.$can(Permission.PROMO_CODE_UPDATE_TIMEFRAME);
  }

  get teedeliTypes() {
    return [
      {
        text: this.$t('promo.types.mostExpensiveArticleDiscountRelative'),
        value: PromoCodeType.Most_Expensive_Article_Discount_Relative,
      },
      {
        text: this.$t('promo.types.teedeliPremium'),
        value: PromoCodeType.Teedeli_Premium,
      },
    ];
  }

  get accessibleCustomerGroups() {
    if (this.$isAdmin()) {
      return this.customerGroups;
    }

    return this.customerGroups.filter(
      (group: { value: string | CustomerGroup; text: string | TranslateResult }) =>
        group.value === '' || group.value === this.customerGroup,
    );
  }

  get accessibleLinkedCustomerGroups() {
    if (this.$isAdmin()) {
      return this.customerGroups;
    }

    return this.customerGroups.filter(
      (group: { value: string | CustomerGroup; text: string | TranslateResult }) =>
        group.value === '' || group.value === this.linkedCustomerGroup,
    );
  }

  get types() {
    return this.typeItems;
  }

  get mainCategoryTypes() {
    return Object.values(MainCategoryType).map((type) => {
      return { text: this.$t(`mainCategories.${type}`), value: type };
    });
  }

  get germanStates() {
    return [
      'Berlin',
      'Bayern',
      'Niedersachsen',
      'Baden-Württemberg',
      'Rheinland-Pfalz',
      'Sachsen',
      'Thüringen',
      'Hessen',
      'Nordrhein-Westfalen',
      'Sachsen-Anhalt',
      'Brandenburg',
      'Mecklenburg-Vorpommern',
      'Hamburg',
      'Schleswig-Holstein',
      'Saarland',
      'Bremen',
    ];
  }

  get austriaStates() {
    return [
      'Burgenland',
      'Kärnten',
      'Niederösterreich',
      'Oberösterreich',
      'Salzburg',
      'Steiermark',
      'Tirol',
      'Vorarlberg',
      'Wien',
    ];
  }

  get tagItems() {
    return this.tags.map((t: Tag) => {
      return {
        value: t._id,
        text: this.$options.filters!.localized(t.name),
      };
    });
  }

  get items() {
    if (this.editing) {
      return this.updateItems;
    }

    return this.teedeli ? this.createTeedeliItems : this.createItems;
  }

  get updateItems() {
    return [
      { name: 'name', type: InputType.Text, label: 'promo.form.name', rules: 'required' },
      {
        name: 'description',
        type: InputType.Language,
        useTextarea: true,
        label: 'promo.form.description',
      },
      ...(this.canUpdateTimeframe
        ? [
            {
              name: 'range',
              type: InputType.DateRange,
              rules: 'required',
              hideEnd: this.isUnlimited,
            },
          ]
        : []),
      {
        name: 'venues',
        type: InputType.Autocomplete,
        label: 'promo.form.venues',
        multiple: true,
        selectAll: true,
        itemValue: '_id',
        itemText: 'name',
        items: this.venues,
        filter: venueFilter,
        change: this.onVenueChange,
      },
      {
        name: 'piggyRewardUuid',
        type: InputType.Select,
        label: 'Piggy Reward Uuid',
        items: this.rewards,
        itemText: 'title',
        itemValue: 'uuid',
        visible: !!this.rewards?.length,
        clearable: true,
      },
      {
        name: 'type',
        type: InputType.Select,
        label: 'promo.form.type',
        items: this.types,
        visible: false,
        disabled: true,
      },
      {
        name: 'discountableMainCategories',
        type: InputType.Select,
        multiple: true,
        label: 'promo.form.discountableMainCategories',
        items: this.mainCategoryTypes,
        visible: this.strictAbsoluteOrRelativeValueVisibleCallbackDefault,
      },
      {
        name: 'uploadedGiftCards',
        type: InputType.File,
        label: 'promo.form.uploadGiftCards',
        visible: this.uploadGiftCardsVisibleCallbackDefault,
        default: null,
        accept: '.csv',
      },
      { name: 'mov', type: InputType.Text, label: 'promo.form.mov', default: '0', rules: 'required' },
      {
        name: 'amount',
        type: InputType.Text,
        label: 'promo.form.amount',
        default: '0',
        rules: !this.isUnlimitedAmount ? 'required' : '',
        visible: !this.isUnlimitedAmount,
      },
      {
        name: 'linkedCustomerGroup',
        type: InputType.Select,
        label: 'subscription.form.linkedCustomerGroup',
        items: this.accessibleLinkedCustomerGroups,
        visible: this.accessibleLinkedCustomerGroups?.length > 0,
        change: this.onLinkedCustomerGroupChange,
      },
      {
        name: 'applyOnAnyQuantity',
        type: InputType.Checkbox,
        label: 'promo.form.applyOnAnyQuantity',
        visible: this.applyOnAnyQuantityDefault,
        default: false,
      },
      {
        name: 'isMultipleApplicable',
        type: InputType.Checkbox,
        label: 'promo.form.isMultipleApplicable',
        default: false,
      },
      { name: 'generalPromoCode', type: InputType.Checkbox, label: 'promo.form.generalPromoCode', default: true },
      { name: 'active', type: InputType.Checkbox, label: 'promo.form.active', default: true },
      {
        name: 'registrationRequired',
        type: InputType.Checkbox,
        label: 'promo.form.registrationRequired',
        default: false,
        change: this.onRegistrationRequiredChange,
      },
      {
        name: 'boundToUser',
        type: InputType.Checkbox,
        label: 'promo.form.boundToUser',
        disabled: (form: { registrationRequired: boolean }) => !form.registrationRequired,
        default: false,
      },
      {
        name: 'isAutomatic',
        type: InputType.Checkbox,
        default: false,
        label: 'promo.form.isAutomatic',
      },
      {
        name: 'isPiggyVoucher',
        type: InputType.Checkbox,
        default: false,
        label: 'promo.form.isPiggyVoucher',
      },
    ];
  }

  get createItems() {
    const items: FormItem[] = [
      { name: 'name', type: InputType.Text, label: 'promo.form.name', rules: 'required' },
      { name: 'code', type: InputType.Text, label: 'promo.form.code', rules: 'required' },
      { name: 'lightspeedCode', type: InputType.Text, label: 'promo.form.lightspeedCode' },
      { name: 'simphonyCode', type: InputType.Text, label: 'promo.form.simphonyCode' },
      {
        name: 'description',
        type: InputType.Language,
        useTextarea: true,
        label: 'promo.form.description',
      },
      {
        name: 'range',
        type: InputType.DateRange,
        rules: 'required',
        hideEnd: this.isUnlimited,
      },
      {
        name: 'customerGroup',
        type: InputType.Select,
        label: 'promo.form.customerGroup',
        items: this.accessibleCustomerGroups,
        visible: this.accessibleLinkedCustomerGroups?.length > 0,
        change: this.onCustomerGroupChange,
      },
      {
        name: 'linkedCustomerGroup',
        type: InputType.Select,
        label: 'subscription.form.linkedCustomerGroup',
        items: this.accessibleLinkedCustomerGroups,
        visible: this.accessibleLinkedCustomerGroups?.length > 0,
        change: this.onLinkedCustomerGroupChange,
      },
      {
        name: 'countries',
        type: InputType.Select,
        multiple: true,
        label: 'promo.form.countries',
        items: this.countries,
        change: this.onCountriesChange,
      },
      { name: 'states', type: InputType.Select, multiple: true, label: 'promo.form.states', items: this.getStates },
      {
        name: 'venues',
        type: InputType.Autocomplete,
        label: 'promo.form.venues',
        multiple: true,
        selectAll: true,
        itemValue: '_id',
        itemText: 'name',
        items: this.venues,
        filter: venueFilter,
        change: this.onVenueChange,
      },
      {
        name: 'piggyRewardUuid',
        type: InputType.Select,
        label: 'Piggy Reward Uuid',
        items: this.rewards,
        itemText: 'title',
        itemValue: 'uuid',
        visible: !!this.rewards?.length,
        clearable: true,
      },
      {
        name: 'label',
        type: InputType.Select,
        label: 'promo.form.label',
        items: this.labels,
        default: null,
        itemText: 'name',
        itemValue: '_id',
      },
      {
        name: 'type',
        type: InputType.Select,
        label: 'promo.form.type',
        items: this.types,
        disabled: this.typeDisabledCheck,
        rules: 'required',
        change: this.onTypeChange,
      },
      {
        name: 'discountableMainCategories',
        type: InputType.Select,
        multiple: true,
        label: 'promo.form.discountableMainCategories',
        items: this.mainCategoryTypes,
        visible: this.strictAbsoluteOrRelativeValueVisibleCallbackDefault,
      },
      {
        name: 'value',
        type: InputType.Text,
        label: 'promo.form.discount',
        default: 0,
        visible: this.absoluteValueVisibleCallbackDefault,
        rules: 'required|decimal|greater_than:0',
      },
      {
        name: 'value',
        type: InputType.Text,
        label: 'promo.form.percent',
        default: 0,
        visible: this.relativeValueVisibleCallbackDefault,
        rules: 'required|decimal|greater_than:0|smaller_eq_than:1',
      },
      {
        name: 'article',
        type: InputType.Autocomplete,
        label: 'promo.form.article',
        visible: this.discountArticleVisibleCallbackDefault,
        rules: 'required',
        multiple: true,
        items: this.articleItems,
      },
      {
        name: 'value',
        type: InputType.Autocomplete,
        label: 'promo.form.bogoArticles',
        visible: this.bogoValueVisibleCallbackDefault,
        rules: 'required',
        multiple: true,
        items: this.articleItems,
      },
      {
        name: 'value',
        type: InputType.Autocomplete,
        label: 'promo.form.freeArticle',
        visible: this.freeArticleValueVisibleCallbackDefault,
        rules: 'required',
        multiple: this.isFreeArticleMultipleSelection,
        items: this.articleItems,
      },
      {
        name: 'minQuantity',
        type: InputType.Text,
        label: 'promo.form.minQuantity',
        visible: this.dependencyFreeArticleValueVisibleCallbackDefault,
        rules: 'required|integer|min:1',
        default: 1,
      },
      {
        name: 'maxQuantity',
        type: InputType.Text,
        label: 'promo.form.maxQuantity',
        visible: this.multipleArticleVisibleCallbackDefault,
        rules: 'required|integer|min:1',
        default: 1,
      },
      {
        name: 'tags',
        type: InputType.Select,
        label: 'article.form.tags',
        visible: this.articleTagVisibleCallbackDefault,
        items: this.tagItems,
        rules: 'required',
        multiple: true,
      },
      {
        name: 'usageFrequency',
        type: InputType.Select,
        label: 'promo.form.usageFrequency',
        items: this.usageFrequencyTypes,
        rules: 'required',
      },
      {
        name: 'uploadedGiftCards',
        type: InputType.File,
        label: 'promo.form.uploadGiftCards',
        visible: this.uploadGiftCardsVisibleCallbackDefault,
        rules: 'required',
        default: null,
        accept: '.csv',
      },
      { name: 'mov', type: InputType.Text, label: 'promo.form.mov', default: '0', rules: 'required' },
      {
        name: 'amount',
        type: InputType.Text,
        label: 'promo.form.amount',
        default: '0',
        rules: !this.isUnlimitedAmount ? 'required' : '',
        visible: !this.isUnlimitedAmount,
      },
      {
        name: 'applyOnAnyQuantity',
        type: InputType.Checkbox,
        label: 'promo.form.applyOnAnyQuantity',
        visible: this.applyOnAnyQuantityDefault,
        default: false,
      },
      {
        name: 'isMultipleApplicable',
        type: InputType.Checkbox,
        label: 'promo.form.isMultipleApplicable',
        default: false,
      },
      { name: 'isMaster', type: InputType.Checkbox, label: 'promo.form.isMaster', default: false },
      { name: 'generalPromoCode', type: InputType.Checkbox, label: 'promo.form.generalPromoCode', default: true },
      {
        name: 'registrationRequired',
        type: InputType.Checkbox,
        label: 'promo.form.registrationRequired',
        default: false,
        change: this.onRegistrationRequiredChange,
      },
      {
        name: 'boundToUser',
        type: InputType.Checkbox,
        label: 'promo.form.boundToUser',
        disabled: (form: { registrationRequired: boolean }) => !form.registrationRequired,
        default: false,
      },
      { name: 'active', type: InputType.Checkbox, label: 'promo.form.active', default: true },
      {
        name: 'useCustomerRedemptionCode',
        type: InputType.Checkbox,
        label: 'promo.form.useCustomerRedemptionCodes',
        default: false,
        disabled: this.editing,
      },
      {
        name: 'isAutomatic',
        type: InputType.Checkbox,
        default: false,
        label: 'promo.form.isAutomatic',
      },
      {
        name: 'isPiggyVoucher',
        type: InputType.Checkbox,
        default: false,
        label: 'promo.form.isPiggyVoucher',
      },
    ];

    if (this.multiple) {
      return [
        ...items.filter((i: FormItem) => i.name !== 'code'),
        {
          name: 'generateAmount',
          type: InputType.Text,
          default: 10,
          label: 'promo.form.generateAmount',
          rules: 'required|integer',
        },
      ];
    }

    return items;
  }

  get createTeedeliItems() {
    const items: FormItem[] = [
      { name: 'name', type: InputType.Text, label: 'promo.form.name', rules: 'required' },
      {
        name: 'range',
        type: InputType.DateRange,
        rules: 'required',
        hideEnd: this.isUnlimited,
      },
      {
        name: 'customerGroup',
        type: InputType.Select,
        default: CustomerGroupEnum.TEEDELI,
        disabled: true,
        label: 'promo.form.customerGroup',
        items: this.accessibleLinkedCustomerGroups,
        visible: this.accessibleLinkedCustomerGroups?.length > 0,
        change: this.onCustomerGroupChange,
      },
      {
        name: 'venues',
        type: InputType.Autocomplete,
        multiple: true,
        selectAll: true,
        label: 'promo.form.venue',
        itemValue: '_id',
        itemText: 'name',
        items: this.venues,
        filter: venueFilter,
        rules: 'required',
        change: this.onVenueChange,
      },
      {
        name: 'piggyRewardUuid',
        type: InputType.Select,
        label: 'Piggy Reward Uuid',
        items: this.rewards,
        itemText: 'title',
        itemValue: 'uuid',
        visible: !!this.rewards?.length,
        clearable: true,
      },
      {
        name: 'type',
        type: InputType.Select,
        label: 'promo.form.type',
        items: this.types,
        disabled: this.typeDisabledCheck,
        rules: 'required',
        change: this.onTypeChange,
      },
      {
        name: 'discountableMainCategories',
        type: InputType.Select,
        multiple: true,
        label: 'promo.form.discountableMainCategories',
        items: this.mainCategoryTypes,
        visible: this.strictAbsoluteOrRelativeValueVisibleCallbackDefault,
      },
      {
        name: 'value',
        type: InputType.Text,
        label: 'promo.form.discount',
        default: 0,
        visible: this.absoluteValueVisibleCallbackDefault,
        rules: 'required|decimal|greater_than:0',
      },
      {
        name: 'value',
        type: InputType.Text,
        label: 'promo.form.percent',
        default: 0,
        visible: this.relativeValueVisibleCallbackDefault,
        rules: 'required|decimal|greater_than:0|smaller_eq_than:1',
      },
      {
        name: 'article',
        type: InputType.Autocomplete,
        label: 'promo.form.article',
        visible: this.discountArticleVisibleCallbackDefault,
        rules: 'required',
        multiple: this.isMultipleFreeArticleMultipleSelection,
        items: this.articleItems,
      },
      {
        name: 'value',
        type: InputType.Autocomplete,
        label: 'promo.form.bogoArticles',
        visible: this.bogoValueVisibleCallbackDefault,
        rules: 'required',
        multiple: true,
        items: this.articleItems,
      },
      {
        name: 'value',
        type: InputType.Autocomplete,
        label: 'promo.form.freeArticle',
        visible: this.freeArticleValueVisibleCallbackDefault,
        rules: 'required',
        multiple: this.isFreeArticleMultipleSelection,
        items: this.articleItems,
      },
      {
        name: 'minQuantity',
        type: InputType.Text,
        label: 'promo.form.minQuantity',
        visible: this.dependencyFreeArticleValueVisibleCallbackDefault,
        rules: 'required|integer|min:1',
        default: 1,
      },
      {
        name: 'maxQuantity',
        type: InputType.Text,
        label: 'promo.form.maxQuantity',
        visible: this.multipleArticleVisibleCallbackDefault,
        rules: 'required|integer|min:1',
        default: 1,
      },
      {
        name: 'tags',
        type: InputType.Select,
        label: 'article.form.tags',
        visible: this.articleTagVisibleCallbackDefault,
        items: this.tagItems,
        multiple: true,
      },
      {
        name: 'uploadedGiftCards',
        type: InputType.File,
        label: 'promo.form.uploadGiftCards',
        visible: this.uploadGiftCardsVisibleCallbackDefault,
        rules: 'required',
        default: null,
        accept: '.csv',
      },
      { name: 'mov', type: InputType.Text, label: 'promo.form.mov', default: '0', rules: 'required' },
      {
        name: 'amountPromo',
        type: InputType.Text,
        label: 'promo.form.amountTeedeli',
        default: '0',
        rules: 'required',
      },
      {
        name: 'amount',
        type: InputType.Text,
        label: 'promo.form.amountUsesTeedeli',
        default: '0',
        rules: !this.isUnlimitedAmount ? 'required' : '',
        visible: !this.isUnlimitedAmount,
      },
      {
        name: 'applyOnAnyQuantity',
        type: InputType.Checkbox,
        label: 'promo.form.applyOnAnyQuantity',
        visible: this.applyOnAnyQuantityDefault,
        default: false,
      },
      {
        name: 'isMultipleApplicable',
        type: InputType.Checkbox,
        label: 'promo.form.isMultipleApplicable',
        default: false,
      },
      { name: 'generalPromoCode', type: InputType.Checkbox, label: 'promo.form.generalPromoCode', default: true },
      { name: 'active', type: InputType.Checkbox, label: 'promo.form.active', default: true },
      {
        name: 'useCustomerRedemptionCodes',
        type: InputType.Checkbox,
        label: 'promo.form.useCustomerRedemptionCodes',
        default: false,
        disabled: this.editing,
      },
      {
        name: 'isAutomatic',
        type: InputType.Checkbox,
        default: false,
        label: 'promo.form.isAutomatic',
      },
      {
        name: 'isPiggyVoucher',
        type: InputType.Checkbox,
        default: false,
        label: 'promo.form.isPiggyVoucher',
      },
    ];

    return items;
  }

  @Emit('venues')
  public onVenueChange(
    val: string[],
    form: { countries: string[]; customerGroup: CustomerGroup; linkedCustomerGroup: CustomerGroup },
  ) {
    return {
      venues: val,
      countries: form.countries,
      customerGroup: form.customerGroup,
      linkedCustomerGroup: form.linkedCustomerGroup,
    };
  }

  @Emit('countries')
  public onCountriesChange(
    val: string[],
    form: { countries: string[]; customerGroup: CustomerGroup; linkedCustomerGroup: CustomerGroup },
  ) {
    return {
      countries: form.countries,
      customerGroup: form.customerGroup,
      linkedCustomerGroup: form.linkedCustomerGroup,
    };
  }

  @Emit('customerGroup')
  public onCustomerGroupChange(
    val: CustomerGroup[],
    form: { countries: string[]; customerGroup: CustomerGroup; linkedCustomerGroup: CustomerGroup },
  ) {
    if (val.toString() === CustomerGroupEnum.TEEDELI) {
      this.typeItems = [...this.origTypeItems, ...this.teedeliTypes];
    } else {
      this.typeItems = [...this.origTypeItems];
    }
    return {
      countries: form.countries,
      customerGroup: form.customerGroup,
      linkedCustomerGroup: form.linkedCustomerGroup,
    };
  }

  @Emit('linkedCustomerGroup')
  public onLinkedCustomerGroupChange(
    val: CustomerGroup[],
    form: { countries: string[]; customerGroup: CustomerGroup; linkedCustomerGroup: CustomerGroup },
  ) {
    if (val.toString() === CustomerGroupEnum.TEEDELI) {
      this.typeItems = [...this.origTypeItems, ...this.teedeliTypes];
    } else {
      this.typeItems = [...this.origTypeItems];
    }
    return {
      countries: form.countries,
      customerGroup: form.customerGroup,
      linkedCustomerGroup: form.linkedCustomerGroup,
    };
  }

  public getStates(form: { countries: string[] }) {
    if (form && form.countries) {
      const states: string[] = [];
      if (form.countries.indexOf(Country.DE) > -1) {
        states.push(...this.germanStates);
      }
      if (form.countries.indexOf(Country.AU) > -1) {
        states.push(...this.austriaStates);
      }

      return states;
    }

    return [];
  }

  get articleItems() {
    return this.articles.map((a: Article) => {
      return {
        value: a._id,
        text: `${this.$options.filters!.localized(a.name)} (${a.number})`,
      };
    });
  }

  get usageFrequencyTypes() {
    return [
      { text: this.$t('promo.usageFrequency.any'), value: PromoCodeUsageFrequency.Any },
      { text: this.$t('promo.usageFrequency.daily'), value: PromoCodeUsageFrequency.Daily },
      { text: this.$t('promo.usageFrequency.weekly'), value: PromoCodeUsageFrequency.Weekly },
      { text: this.$t('promo.usageFrequency.monthly'), value: PromoCodeUsageFrequency.Monthly },
    ];
  }

  public strictAbsoluteOrRelativeValueVisibleCallbackDefault(form: { type?: PromoCodeType }) {
    return !!(form.type && [PromoCodeType.Absolute, PromoCodeType.Relative].indexOf(form.type!) > -1);
  }

  public absoluteValueVisibleCallbackDefault(form: { type?: PromoCodeType }) {
    return !!(
      form.type &&
      [
        PromoCodeType.Absolute,
        PromoCodeType.Absolute_Article,
        PromoCodeType.Single_Absolute_Article,
        PromoCodeType.Multiple_Absolute_Article,
      ].indexOf(form.type!) > -1
    );
  }

  public relativeValueVisibleCallbackDefault(form: { type?: PromoCodeType }) {
    return !!(
      form.type &&
      [
        PromoCodeType.Relative,
        PromoCodeType.Relative_Article,
        PromoCodeType.Teedeli_Premium,
        PromoCodeType.Most_Expensive_Article_Discount_Relative,
        PromoCodeType.Single_Relative_Article,
        PromoCodeType.Multiple_Relative_Article,
        PromoCodeType.Cheapest_Half_Article_Discount_Relative,
      ].indexOf(form.type!) > -1
    );
  }

  public discountArticleVisibleCallbackDefault(form: { type?: PromoCodeType }) {
    return !!(
      form.type &&
      [
        PromoCodeType.Relative_Article,
        PromoCodeType.Absolute_Article,
        PromoCodeType.Dependency_Free_Article,
        PromoCodeType.Cheapest_Free_Article,
        PromoCodeType.Single_Absolute_Article,
        PromoCodeType.Single_Relative_Article,
        PromoCodeType.Multiple_Absolute_Article,
        PromoCodeType.Multiple_Relative_Article,
        PromoCodeType.Cheapest_Half_Article_Discount_Relative,
        PromoCodeType.Multiple_Free_Articles,
      ].indexOf(form.type!) > -1
    );
  }

  public applyOnAnyQuantityDefault(form: { type?: PromoCodeType }) {
    return !!(form.type && [PromoCodeType.Relative_Article, PromoCodeType.Absolute_Article].indexOf(form.type!) > -1);
  }

  public bogoValueVisibleCallbackDefault(form: { type?: PromoCodeType }) {
    return !!(form.type && [PromoCodeType.Bogo].indexOf(form.type!) > -1);
  }

  public uploadGiftCardsVisibleCallbackDefault(form: { type?: PromoCodeType }) {
    return !!(form.type && [PromoCodeType.Gift_Card].indexOf(form.type!) > -1);
  }

  public freeArticleValueVisibleCallbackDefault(form: { type?: PromoCodeType }) {
    return !!(
      form.type &&
      [PromoCodeType.Free_Article, PromoCodeType.Coupon, PromoCodeType.Dependency_Free_Article].indexOf(form.type!) > -1
    );
  }

  public dependencyFreeArticleValueVisibleCallbackDefault(form: { type?: PromoCodeType }) {
    return !!(
      form.type && [PromoCodeType.Dependency_Free_Article, PromoCodeType.Cheapest_Free_Article].indexOf(form.type!) > -1
    );
  }

  public multipleArticleVisibleCallbackDefault(form: { type?: PromoCodeType }) {
    return !!(
      form.type &&
      [
        PromoCodeType.Multiple_Absolute_Article,
        PromoCodeType.Multiple_Relative_Article,
        PromoCodeType.Unlock_Articles,
        PromoCodeType.Multiple_Free_Articles,
      ].indexOf(form.type!) > -1
    );
  }

  public articleTagVisibleCallbackDefault(form: { type?: PromoCodeType }) {
    return !!(form.type && [PromoCodeType.Unlock_Articles].indexOf(form.type!) > -1);
  }

  public typeDisabledCheck() {
    return this.editing;
  }

  public onTypeChange(type: PromoCodeType) {
    const isDependencyFreeArticle: boolean = type === PromoCodeType.Dependency_Free_Article;
    const isMultipleFreeArticles: boolean = type === PromoCodeType.Multiple_Free_Articles;

    this.isFreeArticleMultipleSelection = !isDependencyFreeArticle;
    this.isMultipleFreeArticleMultipleSelection = isMultipleFreeArticles;

    this.$refs.form.setValue('value', isDependencyFreeArticle ? null : []);

    if (this.teedeli) {
      this.$refs.form.setValue('article', !isMultipleFreeArticles ? null : []);
    }
  }

  public getData() {
    const data: Partial<PromoCode> = { ...this.$refs.form.form, ...this.$refs.form.form.range };

    if (data.piggyRewardUuid === undefined) {
      data.piggyRewardUuid = '';
    }

    if (
      [
        PromoCodeType.Absolute_Article,
        PromoCodeType.Relative_Article,
        PromoCodeType.Single_Absolute_Article,
        PromoCodeType.Single_Relative_Article,
        PromoCodeType.Multiple_Absolute_Article,
        PromoCodeType.Multiple_Relative_Article,
        PromoCodeType.Cheapest_Half_Article_Discount_Relative,
      ].includes(data.type!)
    ) {
      data.value = {
        discount: data.value,
        articles: data.article,
        ...([PromoCodeType.Multiple_Absolute_Article, PromoCodeType.Multiple_Relative_Article].includes(data.type!)
          ? { maxQuantity: data.maxQuantity }
          : {}),
      };
    }

    if ([PromoCodeType.Dependency_Free_Article].includes(data.type!)) {
      data.value = {
        freeArticle: data.value,
        articles: data.article,
        minQuantity: data.minQuantity,
      };
    }

    if ([PromoCodeType.Cheapest_Free_Article].includes(data.type!)) {
      data.value = {
        articles: data.article,
        minQuantity: data.minQuantity,
      };
    }

    if ([PromoCodeType.Unlock_Articles].includes(data.type!)) {
      data.value = {
        tags: data.tags,
        maxQuantity: data.maxQuantity,
      };
    }

    if ([PromoCodeType.Multiple_Free_Articles].includes(data.type!)) {
      data.value = {
        articles: data.article,
        maxQuantity: data.maxQuantity,
      };
    }

    return data;
  }

  public onRegistrationRequiredChange(required: boolean) {
    if (!required) {
      this.$refs.form.setValue('boundToUser', false);
      return;
    }
  }

  public mounted() {
    if (this.initVals.range && this.initVals.range.to === '2099-12-31T00:00:00.000Z') {
      this.isUnlimited = true;
    }

    if (this.initVals.amount === 999999999) {
      this.isUnlimitedAmount = true;
    }
  }

  @Watch('isUnlimited')
  public onIsUnlimitedChange(val: boolean) {
    const currentRange = this.$refs.form?.form?.range || {};
    const fallbackRange = {
      from: currentRange.from || null,
      to: this.initVals.range?.to || currentRange.from || null,
    };
    const newRange = { ...fallbackRange, to: '2099-12-31' };

    this.$refs.form.setValue('range', val ? newRange : fallbackRange);
  }

  @Watch('isUnlimitedAmount')
  public onIsUnlimitedAmountChange(val: boolean) {
    this.$refs.form.setValue('amount', val ? '999999999' : '0');
  }
}
